import type { UiConformancePanelModel } from './ui-ontology-panel-model'

export type FallbackPromotionStatus = 'pass' | 'blocked' | 'review' | 'ready'

export interface FallbackPromotionControlTowerModel {
  status: FallbackPromotionStatus
  summary: {
    candidateFacts: number
    quarantinedAntiPatterns: number
    promotionBlocked: number
    prReadyValidatedCandidates: number
  }
  sections: FallbackPromotionSection[]
  actions?: FallbackPromotionActions
}

export interface FallbackPromotionActions {
  reviewedPrUrl?: string
  evidenceUrl?: string
  quarantineArchiveUrl?: string
}

export interface FallbackPromotionSection {
  id: 'candidate-facts' | 'quarantine' | 'promotion-blocked' | 'pr-ready'
  title: string
  items: FallbackPromotionItem[]
}

export interface FallbackPromotionItem {
  id: string
  status: 'candidate' | 'quarantine' | 'blocked' | 'pr-ready' | 'archived'
  title: string
  message: string
  jsonPath?: string
  path?: Array<string | number>
  blockName?: string
  actionFlowName?: string
  confidence?: number
  reviewStatus?: 'pending-review' | 'quarantine' | 'accepted' | string
  source?: string
  prUrl?: string
  patchPlanId?: string
}

export interface ScopedFallbackPromotionModel {
  scope: string
  path: Array<string | number>
  model: FallbackPromotionControlTowerModel
}

export interface FallbackPromotionObservation {
  id?: string
  kind?: string
  observedAt?: string
  forSemanticType?: string
  forPattern?: string
  pickedExample?: string | null
  context?: Record<string, unknown>
  [key: string]: unknown
}

export interface RuntimeActionFlowEvidenceInput {
  kind: string
  label: string
  value: string
  path: Array<string | number>
  detail?: string
}

const MODEL_KEYS = [
  'fallbackPromotionControlTower',
  'promotionControlTower',
  'fallbackPromotionSurface',
  'fallbackEvidenceAggregation',
  'uiOntologyPromotion',
] as const

export function readFallbackPromotionControlTowerModel(
  source: unknown,
): FallbackPromotionControlTowerModel | null {
  const candidates = [
    source,
    readRecord(source)?.metadata,
    readRecord(readRecord(source)?.block)?.metadata,
  ]

  for (const candidate of candidates) {
    const record = readRecord(candidate)
    if (!record) continue

    for (const key of MODEL_KEYS) {
      const direct = normalizeControlTowerModel(record[key])
      if (direct) return direct
    }

    const derived = deriveControlTowerModel(record)
    if (derived) return derived
  }

  return null
}

export function collectFallbackPromotionControlTowerModels(
  projectJson: Record<string, unknown>,
): ScopedFallbackPromotionModel[] {
  const models: ScopedFallbackPromotionModel[] = []
  const projectModel = readFallbackPromotionControlTowerModel(projectJson)
  if (projectModel) {
    models.push({ scope: 'Project', path: [], model: projectModel })
  }

  const blocks = readRecord(projectJson.blocks) ?? {}
  for (const [blockName, block] of Object.entries(blocks)) {
    const model = readFallbackPromotionControlTowerModel(block)
    if (model) {
      models.push({
        scope: blockName,
        path: ['blocks', blockName],
        model,
      })
    }
  }

  return models
}

export function buildFallbackPromotionControlTowerModelFromUiConformance(
  model: UiConformancePanelModel | null,
  options: { scope?: string; path?: Array<string | number> } = {},
): ScopedFallbackPromotionModel[] {
  if (!model || model.status === 'pass') return []

  const candidateFacts: FallbackPromotionItem[] = []
  const quarantine: FallbackPromotionItem[] = []
  const blocked: FallbackPromotionItem[] = []

  for (const section of model.sections) {
    for (const [index, item] of section.items.entries()) {
      const base: FallbackPromotionItem = {
        id: `ui-conformance:${section.id}:${item.code}:${index}`,
        status: 'blocked',
        title: item.schemaField ?? item.code,
        message: item.message,
        jsonPath: item.schemaField,
        path: item.schemaField ? item.schemaField.split('.') : options.path,
        blockName: typeof options.path?.[1] === 'string' ? options.path[1] : undefined,
        confidence: item.generatorImpact === 'fallback-negative-constraint' ? 0.4 : undefined,
        reviewStatus: item.generatorImpact === 'fallback-negative-constraint' ? 'quarantine' : 'pending-review',
        source: 'studio-ui-conformance',
      }

      if (item.generatorImpact === 'fallback-negative-constraint') {
        if (section.id === 'capability-mismatches') {
          candidateFacts.push({
            ...base,
            status: 'candidate',
            title: `Renderer capability: ${base.title}`,
          })
        } else {
          quarantine.push({
            ...base,
            status: 'quarantine',
            title: `Quarantine: ${base.title}`,
          })
        }
        continue
      }

      blocked.push({
        ...base,
        status: 'blocked',
        title: `Blocked: ${base.title}`,
      })
    }
  }

  const sections = buildSections(candidateFacts, quarantine, blocked, [])
  const summary = normalizeSummary(null, sections)
  if (!summary || Object.values(summary).every((count) => count === 0)) return []

  return [{
    scope: options.scope ?? 'UI conformance',
    path: options.path ?? [],
    model: {
      status: normalizeStatus(model.status === 'fallback' ? 'review' : 'blocked', summary),
      summary,
      sections,
    },
  }]
}

export function buildFallbackPromotionControlTowerModelFromActionFlowEvidence(
  evidence: RuntimeActionFlowEvidenceInput[],
  options: {
    scope?: string
    path?: Array<string | number>
    actionFlowName?: string
    evidenceUrl?: string
  } = {},
): ScopedFallbackPromotionModel[] {
  if (evidence.length === 0) return []

  const items: FallbackPromotionItem[] = evidence.map((row, index) => {
    const blockName = typeof row.path[1] === 'string' && row.path[0] === 'blocks'
      ? row.path[1]
      : undefined
    return {
      id: `actionflow-runtime:${row.kind}:${index}:${row.value}`,
      status: 'pr-ready',
      title: `${row.kind}: ${row.label}`,
      message: row.detail
        ? `Preview runtime evidence links ${row.value} (${row.detail}).`
        : `Preview runtime evidence links ${row.value}.`,
      jsonPath: formatControlTowerJsonPath(row.path),
      path: row.path,
      blockName,
      actionFlowName: options.actionFlowName,
      confidence: 1,
      reviewStatus: 'runtime-validated',
      source: 'preview-actionflow-runtime',
    }
  })

  const sections = buildSections([], [], [], items)
  const summary = normalizeSummary(null, sections)
  if (!summary) return []

  return [{
    scope: options.scope ?? 'Preview ActionFlow runtime',
    path: options.path ?? [],
    model: {
      status: normalizeStatus('ready', summary),
      summary,
      sections,
      actions: options.evidenceUrl ? { evidenceUrl: options.evidenceUrl } : undefined,
    },
  }]
}

export function parseFallbackPromotionObservationsPayload(payload: unknown): FallbackPromotionObservation[] {
  const candidates = [
    payload,
    readRecord(payload)?.data,
    readRecord(payload)?.observations,
    readRecord(payload)?.items,
    readRecord(payload)?.results,
  ]

  for (const candidate of candidates) {
    const values = readArray(candidate)
    if (!values) continue
    return values
      .map((value) => readRecord(value))
      .filter((record): record is Record<string, unknown> => Boolean(record))
      .map((record) => record as FallbackPromotionObservation)
  }

  return []
}

export function buildFallbackPromotionControlTowerModelFromObservations(
  observations: FallbackPromotionObservation[],
  actions?: FallbackPromotionActions,
): FallbackPromotionControlTowerModel | null {
  const candidateFacts: FallbackPromotionItem[] = []
  const quarantine: FallbackPromotionItem[] = []
  const blocked: FallbackPromotionItem[] = []
  const prReady: FallbackPromotionItem[] = []

  observations.forEach((observation, index) => {
    const context = readRecord(observation.context) ?? {}
    const reviewStatus = stringOrUndefined(context.reviewStatus ?? observation.reviewStatus)
    const kind = stringValue(observation.kind, 'observation')
    const title = stringValue(
      context.normalizedTarget ?? context.title ?? observation.forSemanticType ?? observation.forPattern ?? observation.pickedExample,
      kind,
    )
    const message = stringValue(
      context.message ?? context.reason ?? context.description,
      `Observed ${kind}`,
    )
    const baseItem: FallbackPromotionItem = {
      id: stringValue(observation.id ?? context.id, `observation:${index}`),
      status: 'candidate',
      title,
      message,
      jsonPath: stringOrUndefined(context.jsonPath),
      path: normalizePath(context.path),
      blockName: stringOrUndefined(context.blockName),
      actionFlowName: stringOrUndefined(context.actionFlowName ?? context.flowName),
      confidence: typeof context.confidence === 'number' ? context.confidence : undefined,
      reviewStatus,
      source: stringOrUndefined(context.source ?? observation.source),
      prUrl: stringOrUndefined(context.prUrl ?? context.reviewedPrUrl),
      patchPlanId: stringOrUndefined(context.patchPlanId ?? context.patchPlan),
    }

    if (isPrReadyObservation(observation, context)) {
      prReady.push({ ...baseItem, status: 'pr-ready' })
      return
    }
    if (isBlockedObservation(observation, context)) {
      blocked.push({ ...baseItem, status: 'blocked' })
      return
    }
    if (isQuarantineObservation(observation, context)) {
      quarantine.push({ ...baseItem, status: 'quarantine' })
      return
    }
    if (isCandidateObservation(observation, context)) {
      candidateFacts.push(baseItem)
    }
  })

  const sections = buildSections(candidateFacts, quarantine, blocked, prReady)
  const summary = normalizeSummary(null, sections)
  if (!summary) return null
  if (Object.values(summary).every((count) => count === 0)) return null

  return {
    status: normalizeStatus(null, summary),
    summary,
    sections,
    actions,
  }
}

function normalizeControlTowerModel(value: unknown): FallbackPromotionControlTowerModel | null {
  const record = readRecord(value)
  if (!record) return null

  const sections = normalizeSections(record.sections)
  const summary = normalizeSummary(record.summary, sections)
  if (!summary || !sections) {
    return deriveControlTowerModel(record)
  }

  return {
    status: normalizeStatus(record.status, summary),
    summary,
    sections,
    actions: normalizeActions(record.actions ?? record),
  }
}

function deriveControlTowerModel(record: Record<string, unknown>): FallbackPromotionControlTowerModel | null {
  const candidateFacts = normalizeItems(
    readArray(record.candidateFacts) ?? readArray(record.fallbackCandidateFacts),
    'candidate',
  )
  const quarantine = normalizeItems(
    readArray(record.quarantinedAntiPatterns) ?? readArray(record.quarantine),
    'quarantine',
  )
  const blocked = normalizeItems(
    readArray(record.promotionBlocked) ?? readArray(record.blockedReasons) ?? readArray(record.negativeConstraints),
    'blocked',
  )
  const prReady = normalizeItems(
    readArray(record.prReadyValidatedCandidates) ?? readArray(record.validatedCandidates),
    'pr-ready',
  )

  const sections = buildSections(candidateFacts, quarantine, blocked, prReady)
  const hasSurface = sections.some((section) => section.items.length > 0)
  if (!hasSurface) return null

  const summary = normalizeSummary(record.summary, sections)
  if (!summary) return null

  return {
    status: normalizeStatus(record.status, summary),
    summary,
    sections,
    actions: normalizeActions(record.actions ?? record),
  }
}

function normalizeSummary(
  value: unknown,
  sections: FallbackPromotionSection[] | null,
): FallbackPromotionControlTowerModel['summary'] | null {
  const record = readRecord(value)
  if (record) {
    return {
      candidateFacts: numberValue(record.candidateFacts),
      quarantinedAntiPatterns: numberValue(record.quarantinedAntiPatterns),
      promotionBlocked: numberValue(record.promotionBlocked),
      prReadyValidatedCandidates: numberValue(record.prReadyValidatedCandidates),
    }
  }

  if (!sections) return null
  return {
    candidateFacts: sectionCount(sections, 'candidate-facts'),
    quarantinedAntiPatterns: sectionCount(sections, 'quarantine'),
    promotionBlocked: sectionCount(sections, 'promotion-blocked'),
    prReadyValidatedCandidates: sectionCount(sections, 'pr-ready'),
  }
}

function normalizeSections(value: unknown): FallbackPromotionSection[] | null {
  const sections = readArray(value)
  if (!sections) return null

  const normalized = sections
    .map((section) => {
      const record = readRecord(section)
      if (!record) return null
      const id = normalizeSectionId(record.id)
      if (!id) return null
      return {
        id,
        title: typeof record.title === 'string' ? record.title : sectionTitle(id),
        items: normalizeItems(readArray(record.items), defaultItemStatus(id)),
      }
    })
    .filter((section): section is FallbackPromotionSection => Boolean(section))

  return normalized.length > 0 ? ensureAllSections(normalized) : null
}

function buildSections(
  candidateFacts: FallbackPromotionItem[],
  quarantine: FallbackPromotionItem[],
  blocked: FallbackPromotionItem[],
  prReady: FallbackPromotionItem[],
): FallbackPromotionSection[] {
  return [
    { id: 'candidate-facts', title: 'Needs evidence', items: candidateFacts },
    { id: 'quarantine', title: 'Quarantined', items: quarantine },
    { id: 'promotion-blocked', title: 'Blocked', items: blocked },
    { id: 'pr-ready', title: 'Ready for patch', items: prReady },
  ]
}

function ensureAllSections(sections: FallbackPromotionSection[]): FallbackPromotionSection[] {
  const byId = new Map(sections.map((section) => [section.id, section]))
  return buildSections(
    byId.get('candidate-facts')?.items ?? [],
    byId.get('quarantine')?.items ?? [],
    byId.get('promotion-blocked')?.items ?? [],
    byId.get('pr-ready')?.items ?? [],
  )
}

function normalizeItems(
  values: unknown[] | null,
  defaultStatus: FallbackPromotionItem['status'],
): FallbackPromotionItem[] {
  if (!values) return []
  return values.map((value, index) => normalizeItem(value, defaultStatus, index))
}

function normalizeItem(
  value: unknown,
  defaultStatus: FallbackPromotionItem['status'],
  index: number,
): FallbackPromotionItem {
  if (typeof value === 'string') {
    return {
      id: `${defaultStatus}:${index}`,
      status: defaultStatus,
      title: value,
      message: value,
    }
  }

  const record = readRecord(value) ?? {}
  const status = normalizeItemStatus(record.status ?? record.reviewStatus, defaultStatus)
  const title = stringValue(record.title ?? record.label ?? record.normalizedTarget ?? record.code, `${defaultStatus} ${index + 1}`)
  const message = stringValue(record.message ?? record.reason ?? record.description, title)

  return {
    id: stringValue(record.id, `${status}:${index}`),
    status,
    title,
    message,
    jsonPath: stringOrUndefined(record.jsonPath ?? record.path),
    path: normalizePath(record.path),
    blockName: stringOrUndefined(record.blockName),
    actionFlowName: stringOrUndefined(record.actionFlowName ?? record.flowName),
    confidence: typeof record.confidence === 'number' ? record.confidence : undefined,
    reviewStatus: stringOrUndefined(record.reviewStatus),
    source: stringOrUndefined(record.source),
    prUrl: stringOrUndefined(record.prUrl ?? record.reviewedPrUrl),
    patchPlanId: stringOrUndefined(record.patchPlanId ?? record.patchPlan),
  }
}

function normalizeActions(value: unknown): FallbackPromotionActions | undefined {
  const record = readRecord(value)
  if (!record) return undefined
  const actions = {
    reviewedPrUrl: stringOrUndefined(record.reviewedPrUrl ?? record.openReviewedPrUrl ?? record.prUrl),
    evidenceUrl: stringOrUndefined(record.evidenceUrl ?? record.inspectEvidenceUrl),
    quarantineArchiveUrl: stringOrUndefined(record.quarantineArchiveUrl ?? record.archiveQuarantineUrl),
  }
  return actions.reviewedPrUrl || actions.evidenceUrl || actions.quarantineArchiveUrl ? actions : undefined
}

function normalizeStatus(
  value: unknown,
  summary: FallbackPromotionControlTowerModel['summary'],
): FallbackPromotionStatus {
  if (value === 'pass' || value === 'blocked' || value === 'review' || value === 'ready') return value
  if (summary.promotionBlocked > 0) return 'blocked'
  if (summary.prReadyValidatedCandidates > 0) return 'ready'
  if (summary.candidateFacts > 0 || summary.quarantinedAntiPatterns > 0) return 'review'
  return 'pass'
}

function normalizeSectionId(value: unknown): FallbackPromotionSection['id'] | null {
  if (value === 'candidate-facts' || value === 'quarantine' || value === 'promotion-blocked' || value === 'pr-ready') return value
  if (value === 'candidateFacts' || value === 'needs-evidence') return 'candidate-facts'
  if (value === 'quarantinedAntiPatterns' || value === 'quarantined') return 'quarantine'
  if (value === 'promotionBlocked') return 'promotion-blocked'
  if (value === 'prReadyValidatedCandidates' || value === 'ready-for-patch') return 'pr-ready'
  return null
}

function defaultItemStatus(id: FallbackPromotionSection['id']): FallbackPromotionItem['status'] {
  if (id === 'candidate-facts') return 'candidate'
  if (id === 'quarantine') return 'quarantine'
  if (id === 'promotion-blocked') return 'blocked'
  return 'pr-ready'
}

function normalizeItemStatus(
  value: unknown,
  fallback: FallbackPromotionItem['status'],
): FallbackPromotionItem['status'] {
  if (value === 'candidate' || value === 'quarantine' || value === 'blocked' || value === 'pr-ready' || value === 'archived') return value
  if (value === 'pending-review' || value === 'needs-evidence') return 'candidate'
  if (value === 'accepted') return 'pr-ready'
  if (value === 'quarantined') return 'quarantine'
  if (value === 'ready-for-patch') return 'pr-ready'
  return fallback
}

function sectionTitle(id: FallbackPromotionSection['id']): string {
  if (id === 'candidate-facts') return 'Needs evidence'
  if (id === 'quarantine') return 'Quarantined'
  if (id === 'promotion-blocked') return 'Blocked'
  return 'Ready for patch'
}

function sectionCount(sections: FallbackPromotionSection[], id: FallbackPromotionSection['id']): number {
  return sections.find((section) => section.id === id)?.items.length ?? 0
}

function normalizePath(value: unknown): Array<string | number> | undefined {
  if (!Array.isArray(value)) return undefined
  const path = value.filter((part): part is string | number =>
    typeof part === 'string' || typeof part === 'number',
  )
  return path.length > 0 ? path : undefined
}

function formatControlTowerJsonPath(path: readonly (string | number)[]): string {
  return path.reduce<string>((acc, segment) => {
    if (typeof segment === 'number') return `${acc}[${segment}]`
    return /^[A-Za-z_$][\w$]*$/.test(segment)
      ? `${acc}.${segment}`
      : `${acc}[${JSON.stringify(segment)}]`
  }, '$')
}

function readRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null
}

function readArray(value: unknown): unknown[] | null {
  return Array.isArray(value) ? value : null
}

function numberValue(value: unknown): number {
  return typeof value === 'number' ? value : 0
}

function stringValue(value: unknown, fallback: string): string {
  return typeof value === 'string' && value.length > 0 ? value : fallback
}

function stringOrUndefined(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined
}

function isCandidateObservation(
  observation: FallbackPromotionObservation,
  context: Record<string, unknown>,
): boolean {
  const text = searchableText(observation, context)
  return /candidate|fallback|promotion|ontology-gap|renderer-unsupported/i.test(text)
}

function isQuarantineObservation(
  observation: FallbackPromotionObservation,
  context: Record<string, unknown>,
): boolean {
  const text = searchableText(observation, context)
  return context.reviewStatus === 'quarantine' || /quarantine|anti-pattern|unsafe|raw-style/i.test(text)
}

function isBlockedObservation(
  observation: FallbackPromotionObservation,
  context: Record<string, unknown>,
): boolean {
  const text = searchableText(observation, context)
  return context.promotionRequired === true || /promotion-blocked|blocked|canonical-write/i.test(text)
}

function isPrReadyObservation(
  observation: FallbackPromotionObservation,
  context: Record<string, unknown>,
): boolean {
  const text = searchableText(observation, context)
  return Boolean(context.prUrl ?? context.reviewedPrUrl) || context.reviewStatus === 'accepted' || /pr-ready|patch-plan|validated-candidate/i.test(text)
}

function searchableText(
  observation: FallbackPromotionObservation,
  context: Record<string, unknown>,
): string {
  return [
    observation.kind,
    observation.forSemanticType,
    observation.forPattern,
    context.code,
    context.kind,
    context.status,
    context.reviewStatus,
    context.message,
    context.reason,
    context.normalizedTarget,
    context.patchPlanId,
  ].filter(Boolean).join(' ')
}
